import { useRef } from 'react';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useToast } from '@chakra-ui/react';
import fetch from 'cross-fetch';

import { attachmentApi, recordApi } from '../api';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { modifyArrayItemAt } from '../utils/arrayUtil';
import { queryClient } from '../utils/queryClientUtil';

import recordKeys from './models/record/keys';
import { camelCaseApiResponse } from './util';

export const uploadAttachment = async ({
  recordId,
  file
}) => {
  const attachment = await recordApi.createRecordAttachment(recordId, { name: file.name, size: file.size });

  const response = await fetch(attachment.upload_url, {
    method: 'PUT',
    body: file
  });

  if (!response.ok) {
    throw new Error('Failed to upload file to storage');
  }

  // The backend derives the stored object URL from its server-owned s3_key,
  // so we only need to signal that the upload has finished.
  await attachmentApi.updateAttachmentUrl(attachment.id);
};

export const useAttachmentUploader = recordId => {
  const dispatch = useDispatch();
  const toast = useToast();
  const toastIdRef = useRef();
  const input = document.createElement('input');

  input.setAttribute('type', 'file');
  input.setAttribute('accept', '.xlsx,.xls,image/*,.doc,.docx,.txt,.pdf');

  input.onchange = async () => {
    const file = input.files[0];

    dispatch(createToast({
      toastInstance: toast,
      toastRef: toastIdRef,
      title: 'Uploading attachment',
      description: file.name,
      duration: 60000,
      status: 'general'
    }));

    try {
      await uploadAttachment({
        recordId,
        file,
        dispatch
      });

      await queryClient.invalidateQueries(recordKeys.recordAttachments(recordId));

      dispatch(createToast({
        toastInstance: toast,
        toastRef: toastIdRef,
        title: 'Attachment Uploaded Successfully!',
        duration: 5000,
        status: 'success'
      }));
    } catch (error) {
      dispatch(createErrorToast({
        toastInstance: toast,
        toastRef: toastIdRef,
        title: 'Something went wrong with your upload',
        description: error.message,
        duration: 9000,
        error
      }));
    }
  };

  return () => input.click();
};

export const useUpdateAttachmentStatus = ({ recordId, status, options }) => {
  const dispatch = useDispatch();

  let statusActionVerb = 'Reactivating';
  let statusPastTenseVerb = 'Reactivated';

  if (status === 'archived') {
    statusActionVerb = 'Archiving';
    statusPastTenseVerb = 'Archived';
  } else if (status === 'deleted') {
    statusActionVerb = 'Deleting';
    statusPastTenseVerb = 'Deleted';
  }

  return useMutation(
    attachmentId => camelCaseApiResponse(() => attachmentApi.updateAttachmentStatus(attachmentId, status)),
    {
      onMutate: async attachmentId => {
        await queryClient.cancelQueries(recordKeys.recordAttachments(recordId));
        const previousAttachments = queryClient.getQueryData(recordKeys.recordAttachments(recordId));

        const updatedAttachments = modifyArrayItemAt(
          previousAttachments,
          'id',
          attachmentId,
          a => ({ ...a, status })
        );

        queryClient.setQueryData(recordKeys.recordAttachments(recordId), updatedAttachments);

        dispatch(createSuccessToast({ title: `Attachment ${statusPastTenseVerb}!` }));

        return { previousAttachments };
      },
      onError: (error, _attachment, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: `Your attachment was not ${statusActionVerb} successfully.`,
          error
        }));

        queryClient.setQueryData(recordKeys.recordAttachments(recordId), context.previousAttachments);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(recordKeys.recordAttachments(recordId));
      },
      ...options
    }
  );
};
